import React, { useEffect, useCallback } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useLNAuth } from '@shared/context/LNAuthContext';
import { useTheme } from '../context/ThemeContext';
import { isNative } from '@shared/utils/platform';

const navItems = [
  { to: '/dashboard', label: 'Home', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>
  )},
  { to: '/festival', label: 'Festivals', icon: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707"/></svg>
  )},
];

const hapticTap = () => {
  if (!isNative) return;
  import('@capacitor/haptics').then(({ Haptics, ImpactStyle }) => {
    Haptics.impact({ style: ImpactStyle.Light });
  }).catch(() => {});
};

export default function AppLayout({ children }) {
  const location = useLocation();
  const { user } = useLNAuth();
  const { bgColor, syncFromUser } = useTheme();

  // Sync theme color from DB on first load
  useEffect(() => {
    if (user) syncFromUser(user);
  }, [user, syncFromUser]);

  return (
    <div className="flex h-dvh overflow-hidden" style={{ backgroundColor: bgColor, '--ln-bg': bgColor }}>
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-56 flex-shrink-0 border-r border-white/10 pb-5" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.25rem)' }}>
        {/* Logo */}
        <div className="px-5 mb-8">
          <Link to="/dashboard"><img src="/logo-text.png" alt="Loud Neighbors" className="h-10" /></Link>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 space-y-1">
          {navItems.map((item) => {
            const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => { if (item.to === '/dashboard') window.dispatchEvent(new Event('ln-go-home')); if (item.to === '/festival') window.dispatchEvent(new Event('ln-go-festival')); }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  active ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.icon}
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Profile */}
        <div className="px-3 mt-auto">
          <Link
            to="/profile"
            onClick={() => window.dispatchEvent(new Event('ln-go-profile'))}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              location.pathname === '/profile' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white hover:bg-white/5'
            }`}
          >
            {user?.profile_image_url ? (
              <img src={user.profile_image_url} alt="" className="w-6 h-6 rounded-full" />
            ) : (
              <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold text-white">
                {user?.display_name?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <span className="truncate">{user?.display_name || 'Profile'}</span>
          </Link>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden md:[padding-top:env(safe-area-inset-top,0px)]">
        {/* Desktop page header — hidden on home, shown on other pages */}
        {location.pathname !== '/dashboard' && (
          <div className="hidden md:flex items-center justify-between px-8 pt-5 pb-3 flex-shrink-0">
            <h1 className="text-white font-bold text-sm uppercase tracking-wider">
              {navItems.find(n => location.pathname.startsWith(n.to))?.label || ''}
            </h1>
            <Link to="/dashboard"><img src="/logo-icon.png" alt="LN" className="h-10" /></Link>
          </div>
        )}
        {children || <Outlet />}
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 border-t border-white/10 backdrop-blur-lg z-50 ln-nav" style={{ backgroundColor: `${bgColor}ee` }}>
        <div className="flex items-center justify-around pt-2 pb-safe">
          {navItems.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link key={item.to} to={item.to} onClick={() => { hapticTap(); if (item.to === '/dashboard') window.dispatchEvent(new Event('ln-go-home')); if (item.to === '/festival') window.dispatchEvent(new Event('ln-go-festival')); }} className={`flex flex-col items-center gap-0.5 text-[10px] font-medium ${active ? 'text-white' : 'text-white/40'}`}>
                {item.icon}
                {item.label}
              </Link>
            );
          })}
          <Link to="/profile" onClick={() => { hapticTap(); window.dispatchEvent(new Event('ln-go-profile')); }} className={`flex flex-col items-center gap-0.5 text-[10px] font-medium ${location.pathname === '/profile' ? 'text-white' : 'text-white/40'}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
            Profile
          </Link>
        </div>
      </nav>
    </div>
  );
}
